import { buildFunctionUrl } from '../utils';

export const MAX_KNOWLEDGE_UPLOAD_BYTES = 50 * 1024 * 1024;
export const KNOWLEDGE_FILE_ACCEPT = '.txt,.md,.csv,.pdf,.pptx,.docx,.xlsx,.xls';
export const KNOWLEDGE_AVAILABILITY_TIMEOUT_MS = 10000;

const MIME_BY_EXTENSION = Object.freeze({
  txt: 'text/plain',
  md: 'text/markdown',
  csv: 'text/csv',
  pdf: 'application/pdf',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
});

export function isKnowledgeLockerConfigured() {
  return Boolean(String(process.env.REACT_APP_LOCKER_SERVICE_URL || '').trim());
}

function lockerServiceBaseUrl() {
  const value = String(process.env.REACT_APP_LOCKER_SERVICE_URL || '').trim();
  if (!value) {
    throw new Error('Knowledge Locker is not configured for this Restack environment.');
  }
  return value.replace(/\/+$/, '');
}

function extensionForName(fileName) {
  const parts = String(fileName || '').toLowerCase().split('.');
  return parts.length > 1 ? parts.pop() : '';
}

export function mimeTypeForKnowledgeFile(file) {
  return MIME_BY_EXTENSION[extensionForName(file?.name)] || file?.type || 'application/octet-stream';
}

export function validateKnowledgeFile(file) {
  if (!file) return 'Choose a document to upload.';
  const ext = extensionForName(file.name);
  if (!Object.prototype.hasOwnProperty.call(MIME_BY_EXTENSION, ext)) {
    return 'Choose a PDF, PowerPoint, Word, Excel, CSV, Markdown, or text document.';
  }
  if (!Number.isFinite(file.size) || file.size <= 0) return 'The selected document is empty.';
  if (file.size > MAX_KNOWLEDGE_UPLOAD_BYTES) return 'Documents can be at most 50 MB.';
  return null;
}

async function parseResponse(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.error || `Knowledge request failed (${response.status})`);
    error.status = response.status;
    error.code = data?.code || null;
    error.issues = Array.isArray(data?.issues) ? data.issues : [];
    throw error;
  }
  return data;
}

async function lockerRequest(currentUser, path, { method = 'GET', body, signal } = {}) {
  if (!currentUser?.getIdToken) throw new Error('Sign in again to manage firm knowledge.');
  const token = await currentUser.getIdToken();
  const response = await fetch(`${lockerServiceBaseUrl()}/locker${path}`, {
    method,
    signal,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  return parseResponse(response);
}

export async function sha256KnowledgeFile(file) {
  if (!window.crypto?.subtle) {
    throw new Error('Secure file hashing is not available in this browser.');
  }
  const bytes = await file.arrayBuffer();
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (value) => value.toString(16).padStart(2, '0')).join('');
}

export function uploadKnowledgeFile(uploadUrl, file, { signal, onProgress } = {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let settled = false;

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', abortUpload);
      fn(value);
    };
    const abortUpload = () => xhr.abort();

    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', mimeTypeForKnowledgeFile(file));
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && typeof onProgress === 'function') {
        onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
      }
    };
    xhr.onerror = () => finish(reject, new Error('The document upload could not be completed.'));
    xhr.onabort = () => {
      const error = new Error('Upload cancelled.');
      error.name = 'AbortError';
      finish(reject, error);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        finish(resolve, { generation: xhr.getResponseHeader('x-goog-generation') || undefined });
      } else {
        finish(reject, new Error(`The document upload failed (${xhr.status}).`));
      }
    };

    if (signal?.aborted) {
      abortUpload();
      return;
    }
    signal?.addEventListener('abort', abortUpload, { once: true });
    xhr.send(file);
  });
}

export const getKnowledgeOverview = (currentUser, options = {}) =>
  lockerRequest(currentUser, '/overview', options);

export async function getKnowledgeAvailability(
  currentUser,
  {
    signal,
    timeoutMs = KNOWLEDGE_AVAILABILITY_TIMEOUT_MS,
  } = {}
) {
  const controller = new AbortController();
  let timedOut = false;
  const abortFromCaller = () => controller.abort();
  if (signal?.aborted) controller.abort();
  else signal?.addEventListener('abort', abortFromCaller, { once: true });
  const boundedTimeoutMs = Math.max(1000, Math.min(30000, Number(timeoutMs) || KNOWLEDGE_AVAILABILITY_TIMEOUT_MS));
  const timeout = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, boundedTimeoutMs);
  let rejectOnAbort;
  const abortPromise = new Promise((_resolve, reject) => {
    rejectOnAbort = () => {
      const error = new Error('Knowledge Locker setup check was cancelled.');
      error.name = 'AbortError';
      reject(error);
    };
    if (controller.signal.aborted) rejectOnAbort();
    else controller.signal.addEventListener('abort', rejectOnAbort, { once: true });
  });
  try {
    return await Promise.race([
      lockerRequest(currentUser, '/availability', { signal: controller.signal }),
      abortPromise,
    ]);
  } catch (error) {
    if (timedOut) {
      const timeoutError = new Error('Knowledge Locker setup could not be verified in time.');
      timeoutError.name = 'KnowledgeAvailabilityTimeoutError';
      timeoutError.code = 'readiness_timeout';
      throw timeoutError;
    }
    throw error;
  } finally {
    window.clearTimeout(timeout);
    controller.signal.removeEventListener('abort', rejectOnAbort);
    signal?.removeEventListener('abort', abortFromCaller);
  }
}

export const listKnowledgeDocuments = (currentUser, { limit = 50, cursor, signal } = {}) => {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  return lockerRequest(currentUser, `/documents?${params.toString()}`, { signal });
};

export const listKnowledgeJobs = (currentUser, { limit = 50, signal } = {}) =>
  lockerRequest(currentUser, `/jobs?limit=${encodeURIComponent(limit)}`, { signal });

export const createKnowledgeUpload = (currentUser, payload, options = {}) =>
  lockerRequest(currentUser, '/uploads', { method: 'POST', body: payload, ...options });

export const completeKnowledgeUpload = (currentUser, docId, generation, options = {}) =>
  lockerRequest(currentUser, `/documents/${encodeURIComponent(docId)}/complete`, {
    method: 'POST',
    body: generation ? { generation } : {},
    ...options,
  });

export const retryKnowledgeJob = (currentUser, jobId, options = {}) =>
  lockerRequest(currentUser, `/jobs/${encodeURIComponent(jobId)}/retry`, {
    method: 'POST', body: {}, ...options,
  });

export const deleteKnowledgeDocument = (currentUser, docId, options = {}) =>
  lockerRequest(currentUser, `/documents/${encodeURIComponent(docId)}`, {
    method: 'DELETE', ...options,
  });

export async function listKnowledgeActivity(currentUser, { limit = 50, cursorIso, signal } = {}) {
  if (!currentUser?.getIdToken) throw new Error('Sign in again to view firm activity.');
  const token = await currentUser.getIdToken();
  const response = await fetch(buildFunctionUrl('getOrgAuditLog'), {
    method: 'POST',
    signal,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ limit, cursorIso: cursorIso || null }),
  });
  return parseResponse(response);
}

export async function uploadDocumentToKnowledge(currentUser, file, scope, { signal, onProgress, onStage } = {}) {
  const validationError = validateKnowledgeFile(file);
  if (validationError) throw new Error(validationError);
  if (!scope || !['firm', 'project'].includes(scope.type)) throw new Error('Choose where this document belongs.');
  if (scope.type === 'project' && !scope.projectId) throw new Error('Choose a project for this document.');

  onStage?.('hashing');
  const sha256 = await sha256KnowledgeFile(file);
  onStage?.('starting');
  const session = await createKnowledgeUpload(currentUser, {
    fileName: file.name,
    mimeType: mimeTypeForKnowledgeFile(file),
    sizeBytes: file.size,
    sha256,
    scope,
  }, { signal });
  onStage?.('uploading');
  const uploaded = await uploadKnowledgeFile(session.uploadUrl, file, { signal, onProgress });
  onStage?.('queueing');
  const queued = await completeKnowledgeUpload(currentUser, session.docId, uploaded.generation, { signal });
  return { ...session, ...queued };
}

const knowledgeLockerService = {
  getKnowledgeOverview,
  getKnowledgeAvailability,
  listKnowledgeDocuments,
  listKnowledgeJobs,
  createKnowledgeUpload,
  completeKnowledgeUpload,
  retryKnowledgeJob,
  deleteKnowledgeDocument,
  listKnowledgeActivity,
  uploadDocumentToKnowledge,
  uploadKnowledgeFile,
  sha256KnowledgeFile,
  validateKnowledgeFile,
  isKnowledgeLockerConfigured,
};

export default knowledgeLockerService;
